import { SampleDataModel } from '@/http/models/sample-data'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/blog'

const SAMPLE_DATA_KEY = ['sample-data']

/** Whether the store still has the demo's sample records. A missing endpoint reads as "no sample data". */
export const useSampleData = () =>
  useQuery({
    queryKey: SAMPLE_DATA_KEY,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await SampleDataModel.get()
      return res.success && res.data ? res.data : { has_sample: false, counts: {} }
    },
  })

/** Deletes every sample record, then refreshes all cached lists (products, categories…). */
export const useRemoveSampleData = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await SampleDataModel.remove()
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not remove the sample data'))
      return res.data?.counts ?? {}
    },
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

/** "12 products, 4 categories" from the removed counts (zero counts left out). */
export const describeCounts = (counts: Record<string, number>): string =>
  Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([name, n]) => `${n} ${name.replace(/_/g, ' ')}`)
    .join(', ')
